
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Bell } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

// Import our new components
import HeaderLogo from './header/HeaderLogo';
import HeaderSearch from './header/HeaderSearch';
import NotificationBell from './header/NotificationBell';
import HeaderProfile from './header/HeaderProfile';
import { ReaderNavLinks, JournalistNavLinks } from './header/HeaderNavLinks';
import ModeSwitcher from './header/ModeSwitcher';
import MobileMenu from './header/MobileMenu';
import NewArticleButton from './header/NewArticleButton';
import HeaderStyles from './header/HeaderStyles';
import { useHeaderState } from './header/HeaderUtils';
import { toast } from './ui/use-toast';

type UserRole = 'reader' | 'journalist';

interface GlobalHeaderProps {
  userRole?: UserRole;
  userName?: string;
  avatarUrl?: string;
  notificationCount?: number;
  subscriptionStatus?: string;
  walletBalance?: number;
  draftCount?: number;
}

const GlobalHeader = ({
  userRole = 'reader',
  userName,
  avatarUrl,
  notificationCount = 0,
  subscriptionStatus,
  walletBalance,
  draftCount = 0,
}: GlobalHeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, userProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract user info
  const displayName = userName || userProfile?.display_name || user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'User';
  const profileImage = avatarUrl || userProfile?.avatar_url || null;
  
  // Get actual user role from auth context
  const actualUserRole = userProfile?.user_role || user?.user_metadata?.user_role || userRole;
  
  // Get current mode info
  const { isJournalist, isInReaderMode, isInCreatorMode } = useHeaderState(actualUserRole);
  
  const handleModeSwitch = () => {
    if (isInReaderMode) {
      console.log('GlobalHeader - Switching to Creator mode from', location.pathname);
      navigate('/dashboard');
      toast({
        title: "Creator Mode",
        description: "You're now in Creator mode"
      });
    } else if (isInCreatorMode) {
      console.log('GlobalHeader - Switching to Reader mode from', location.pathname);
      navigate('/home');
      toast({
        title: "Reader Mode",
        description: "You're now in Reader mode"
      });
    }
  };

  return (
    <>
      <HeaderStyles />
      <header className="fixed top-0 left-0 right-0 h-16 md:h-16 bg-white border-b border-[#F0F0F5] shadow-sm z-50 px-4 md:px-6">
        <div className="h-full max-w-[1440px] mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <button 
              className="md:hidden mr-4 text-[#1A2033]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            
            <HeaderLogo navigateTo={isInReaderMode || !isJournalist ? '/home' : '/dashboard'} />

            <nav className="hidden md:flex items-center space-x-6">
              {isInReaderMode || !isJournalist ? <ReaderNavLinks /> : <JournalistNavLinks />}
            </nav>
            
            <ModeSwitcher 
              isJournalist={isJournalist} 
              isInReaderMode={isInReaderMode} 
              isInCreatorMode={isInCreatorMode} 
            />
          </div>

          <div className="flex items-center space-x-2 md:space-x-4">
            <HeaderSearch />

            <NewArticleButton isJournalist={isJournalist} />

            <NotificationBell count={notificationCount} />

            <HeaderProfile 
              displayName={displayName}
              profileImage={profileImage}
              notificationCount={notificationCount}
              subscriptionStatus={subscriptionStatus}
              walletBalance={walletBalance}
              draftCount={draftCount}
              isJournalist={isJournalist}
              isInReaderMode={isInReaderMode}
              isInCreatorMode={isInCreatorMode}
            />
          </div>
        </div>

        <MobileMenu 
          isOpen={mobileMenuOpen} 
          isInReaderMode={isInReaderMode} 
          isJournalist={isJournalist}
          onClose={() => setMobileMenuOpen(false)}
        >
          {isInReaderMode || !isJournalist ? <ReaderNavLinks /> : <JournalistNavLinks />}
        </MobileMenu>
      </header>
    </>
  );
};

export default GlobalHeader;
